import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatINR, monthName } from "../lib/api";
import { PageHeader, PageBody, Section, StatCell } from "../components/layout/Page";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Briefcase, Phone, EnvelopeSimple, MapPin } from "@phosphor-icons/react";

const STATUS_COLOR = {
  present: "bg-[#12B886]",
  late: "bg-[#FAB005]",
  wfh: "bg-[#2563EB]",
  half_day: "bg-[#FAB005]/60",
  absent: "bg-[#EFF6FF]",
};

function getInitials(name = "") {
  return name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

function Heatmap({ days }) {
  // group by month for compact display: rows = days-of-week, cols = weeks
  const grouped = useMemo(() => {
    if (!days?.length) return [];
    const months = {};
    days.forEach((d) => {
      const key = d.date.slice(0, 7); // YYYY-MM
      if (!months[key]) months[key] = [];
      months[key].push(d);
    });
    return Object.entries(months);
  }, [days]);

  return (
    <div className="space-y-6" data-testid="heatmap">
      {grouped.map(([ym, monthDays]) => {
        const [y, m] = ym.split("-");
        // 7 rows (Sun..Sat); columns = weeks of the month
        const firstDow = new Date(monthDays[0].date).getDay();
        const cells = Array(firstDow).fill(null).concat(monthDays);
        // chunk into weeks of 7
        const weeks = [];
        for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
        return (
          <div key={ym}>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-2">
              {monthName(Number(m))} {y}
            </div>
            <div className="flex gap-1">
              {weeks.map((wk, wi) => (
                <div key={wi} className="flex flex-col gap-1">
                  {wk.map((d, di) => (
                    <div
                      key={di}
                      title={d ? `${d.date} · ${d.status}` : ""}
                      className={`w-3.5 h-3.5 rounded-sm ${d ? STATUS_COLOR[d.status] || "bg-[#EFF6FF]" : "bg-transparent"}`}
                      data-testid={d ? `heat-${d.date}` : undefined}
                    />
                  ))}
                  {Array(7 - wk.length).fill(null).map((_, fi) => (
                    <div key={`f${fi}`} className="w-3.5 h-3.5 bg-transparent" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-[#64748B] pt-3 border-t border-border">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#12B886] rounded-sm" /> Present</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#FAB005] rounded-sm" /> Late</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#2563EB] rounded-sm" /> WFH</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#EFF6FF] rounded-sm border border-border" /> Absent</span>
      </div>
    </div>
  );
}

export default function EmployeeProfile() {
  const { id } = useParams();
  const [emp, setEmp] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [payslips, setPayslips] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [empRes, hmRes, lvRes, psRes] = await Promise.all([
          api.get(`/employees/${id}`),
          api.get(`/employees/${id}/attendance-heatmap?months=6`),
          api.get(`/leaves`, { params: { employee_id: id } }),
          api.get(`/payroll/payslips`, { params: { employee_id: id } }),
        ]);
        setEmp(empRes.data);
        setHeatmap(hmRes.data);
        setLeaves(lvRes.data);
        setPayslips(psRes.data);
      } catch {
        /* handled by axios interceptor */
      }
    };
    load();
  }, [id]);

  if (!emp) return <PageBody>Loading…</PageBody>;

  return (
    <div data-testid="employee-profile-page">
      <PageHeader
        eyebrow={
          <Link to="/employees" className="inline-flex items-center gap-1 hover:text-[#2563EB]" data-testid="back-to-employees">
            <ArrowLeft size={11} /> Back to roster
          </Link>
        }
        title={emp.full_name}
        subtitle={
          <span className="font-mono text-xs text-[#2563EB] tracking-wider">{emp.employee_code}</span>
        }
      >
        <Badge variant="outline" className="rounded-sm font-mono text-[10px]" data-testid="emp-profile-mode">{emp.work_mode}</Badge>
        <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider">{emp.status}</Badge>
      </PageHeader>

      <PageBody className="space-y-6">
        {/* Identity card */}
        <Section title="Profile">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16 rounded-sm">
              <AvatarFallback className="bg-[#2563EB] text-white text-lg rounded-sm">{getInitials(emp.full_name)}</AvatarFallback>
            </Avatar>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-3 flex-1">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-semibold">Department</div>
                <div className="text-sm mt-0.5 flex items-center gap-1.5"><Briefcase size={12} className="text-[#475569]" />{emp.department || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-semibold">Designation</div>
                <div className="text-sm mt-0.5">{emp.designation || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-semibold">Joining date</div>
                <div className="text-sm mt-0.5 font-mono">{emp.joining_date || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-semibold">Salary</div>
                <div className="text-sm mt-0.5 font-mono">{formatINR(emp.salary)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-semibold">Email</div>
                <div className="text-sm mt-0.5 flex items-center gap-1.5 break-all"><EnvelopeSimple size={12} className="text-[#475569]" />{emp.email}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-semibold">Phone</div>
                <div className="text-sm mt-0.5 flex items-center gap-1.5"><Phone size={12} className="text-[#475569]" />{emp.phone || "—"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-semibold">Address</div>
                <div className="text-sm mt-0.5 flex items-start gap-1.5"><MapPin size={12} className="text-[#475569] mt-1" /><span>{emp.address || "—"}</span></div>
              </div>
            </div>
          </div>
        </Section>

        {/* Tabs */}
        <Tabs defaultValue="attendance">
          <TabsList className="rounded-sm border border-border bg-white p-1">
            <TabsTrigger value="attendance" className="rounded-sm text-xs uppercase tracking-wider" data-testid="tab-attendance">Attendance</TabsTrigger>
            <TabsTrigger value="leaves" className="rounded-sm text-xs uppercase tracking-wider" data-testid="tab-leaves">Leaves</TabsTrigger>
            <TabsTrigger value="payslips" className="rounded-sm text-xs uppercase tracking-wider" data-testid="tab-payslips">Payslips</TabsTrigger>
          </TabsList>

          <TabsContent value="attendance" className="mt-6 space-y-6">
            {heatmap && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border rounded-sm overflow-hidden">
                  <StatCell label="Present" value={heatmap.summary.present || 0} accent="text-[#12B886]" testId="heat-present" />
                  <StatCell label="Late" value={heatmap.summary.late || 0} accent="text-[#FAB005]" testId="heat-late" />
                  <StatCell label="WFH" value={heatmap.summary.wfh || 0} accent="text-[#2563EB]" testId="heat-wfh" />
                  <StatCell label="Half-day" value={heatmap.summary.half_day || 0} accent="text-[#FAB005]" />
                  <StatCell label="Absent" value={heatmap.summary.absent || 0} />
                </div>
                <Section title={`Daily heatmap · ${heatmap.from_date} → ${heatmap.to_date}`}>
                  <Heatmap days={heatmap.days} />
                </Section>
              </>
            )}
          </TabsContent>

          <TabsContent value="leaves" className="mt-6">
            <Section title={`${leaves.length} leave requests`}>
              <ul className="divide-y divide-border -m-5">
                {leaves.map((l) => (
                  <li key={l.id} className="px-5 py-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium uppercase tracking-wider text-xs">{l.leave_type}</div>
                      <div className="text-xs text-[#64748B] font-mono">{l.from_date} → {l.to_date}</div>
                      <div className="text-xs text-[#475569] mt-1">{l.reason}</div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${l.status === "approved" ? "text-[#12B886]" : l.status === "rejected" ? "text-[#F54236]" : "text-[#FAB005]"}`}>{l.status}</span>
                  </li>
                ))}
                {leaves.length === 0 && <li className="px-5 py-8 text-center text-sm text-[#64748B]">No leave applications</li>}
              </ul>
            </Section>
          </TabsContent>

          <TabsContent value="payslips" className="mt-6">
            <Section title={`${payslips.length} payslips`}>
              <ul className="divide-y divide-border -m-5">
                {payslips.map((p) => (
                  <li key={p.id} className="px-5 py-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium">{monthName(p.month)} {p.year}</div>
                      <div className="text-xs text-[#64748B]">Net: {formatINR(p.net)} · Gross: {formatINR(p.gross)}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-semibold">{p.status}</span>
                  </li>
                ))}
                {payslips.length === 0 && <li className="px-5 py-8 text-center text-sm text-[#64748B]">No payslips generated</li>}
              </ul>
            </Section>
          </TabsContent>
        </Tabs>
      </PageBody>
    </div>
  );
}
